
import React, { useState, useEffect } from 'react';
import { ShoppingBag, Star, ShoppingCart, X, CreditCard, Ticket, Sparkles, Check } from 'lucide-react';
import { collection, onSnapshot, addDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { loadStripe } from '@stripe/stripe-js';
import { db, auth } from './firebaseConfig';
import { Product, User } from './types';

const stripePromise = loadStripe("pk_test_YOUR_PUBLISHABLE_KEY");

export const ShopView = ({ t, user, authReady }: { t: any, user: User, authReady: boolean }) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [category, setCategory] = useState('all');
    const [cart, setCart] = useState<Product[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [useReferralDiscount, setUseReferralDiscount] = useState(false);
    const [loadingCheckout, setLoadingCheckout] = useState(false);

    useEffect(() => {
        // 🔑 CRITICAL: Block ALL snapshots until auth is ready
        // Note: "products" is a public collection, but we still wait for auth to be ready
        if (!authReady) return;
        
        // Additional check: verify auth is available (even for public collections)
        if (!auth.currentUser) {
          // Products are public, but we still wait for auth state to be determined
          return;
        }
        
        const unsubscribe = onSnapshot(collection(db, "products"), (snapshot) => {
            const prodList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
            setProducts(prodList);
        }, (error) => {
            // Handle errors gracefully
            if (import.meta.env.DEV && error?.code !== 'permission-denied') {
                console.warn('[SHOP] Error loading products:', error);
            }
            setProducts([]);
        });
        return () => unsubscribe();
    }, [authReady]);

    const filteredProducts = category === 'all' 
        ? products 
        : products.filter(p => p.category === category);

    const addToCart = (product: Product) => {
        setCart([...cart, product]);
        setIsCartOpen(true);
    };

    const removeFromCart = (index: number) => {
        const newCart = [...cart];
        newCart.splice(index, 1);
        setCart(newCart);
    };

    // Calculation Logic
    const referralDiscountPercent = user.referralStats?.currentDiscountPercent || 0;
    
    const calculateSubtotal = () => cart.reduce((sum, item) => sum + item.price, 0);
    
    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        if (!useReferralDiscount || cart.length === 0) return subtotal;
        
        // Find the most expensive eligible product to apply the discount (optimized user value)
        const eligibleItems = cart.filter(p => p.isReferralEligible).sort((a,b) => b.price - a.price);
        if (eligibleItems.length === 0) return subtotal;

        const bestItem = eligibleItems[0];
        const discountAmount = (bestItem.price * referralDiscountPercent) / 100;
        
        return Math.max(0, subtotal - discountAmount);
    };

    const subtotal = calculateSubtotal();
    const finalTotal = calculateTotal();
    const discountApplied = subtotal - finalTotal;

    const handleCheckout = async () => {
        setLoadingCheckout(true);
        try {
            // 1. Log Order
            const orderRef = await addDoc(collection(db, "orders"), {
                userId: user.id,
                items: cart,
                total: finalTotal,
                referralDiscountUsed: useReferralDiscount ? referralDiscountPercent : 0,
                status: 'pending',
                date: serverTimestamp()
            });

            // 2. If 100% discount was used, unlock and reset counters
            // CRITICAL: This unlocks accumulation so user can start earning discounts again
            if (useReferralDiscount && referralDiscountPercent >= 100) {
                const userRef = doc(db, "users", user.id);
                // Unlock accumulation, reset counters, and mark bonus as used
                // This allows the user to start a new accumulation cycle
                await updateDoc(userRef, {
                    'referralStats.referralDiscountLocked': false, // Unlock accumulation
                    'referralStats.referralBonusUsed': true,
                    'referralStats.confirmedSubscriptions': 0, // Reset count for new cycle
                    'referralStats.currentDiscountPercent': 0, // Reset discount
                    'referralStats.discountsUsed': (user.referralStats?.discountsUsed || 0) + 1
                });
                alert("CONGRATS! Free product claimed using your referral power!");
            }

            // Order logged in Firestore - checkout flow
            // NOTE: In production, integrate with Stripe Checkout or payment gateway here
            console.log("[SHOP] Order created:", orderRef.id, "Total:", finalTotal);
            
            // Clear cart and close modal
            setCart([]);
            setIsCartOpen(false);
            
            // Show success message
            alert(t?.shop_order_success || `Order placed successfully! Order ID: ${orderRef.id.substring(0, 8)}...\n\nNote: Payment integration coming soon. This is a demo order.`);

        } catch (error) {
            console.error(error);
            alert("Checkout failed");
        } finally {
            setLoadingCheckout(false);
        }
    };

    return (
        <div className="p-4 pb-24 relative">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">{t.shop_title}</h1>
                <button onClick={() => setIsCartOpen(true)} className="relative bg-slate-800 p-3 rounded-2xl">
                    <ShoppingCart size={24} />
                    {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-bold">{cart.length}</span>}
                </button>
            </div>

            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
                {['all', 'clothing', 'equipment', 'supplements'].map(cat => (
                     <button key={cat} onClick={() => setCategory(cat)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap ${category === cat ? 'bg-white text-black' : 'bg-slate-800 text-slate-400'}`}>
                        {cat === 'all' ? t.shop_cat_all : t[`shop_cat_${cat === 'equipment' ? 'equip' : cat === 'supplements' ? 'supp' : cat}`]}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredProducts.map(product => (
                    <div key={product.id} className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 group flex flex-col relative">
                        {product.isReferralEligible && (
                            <div className="absolute top-2 left-2 z-10 bg-emerald-500 text-[8px] font-black text-white px-2 py-1 rounded-md flex items-center gap-1 shadow-lg">
                                <Sparkles size={10}/> {t.ref_eligible}
                            </div>
                        )}
                        <div className="aspect-square bg-slate-800 overflow-hidden">
                            <img src={product.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt={product.name} />
                        </div>
                        <div className="p-3 flex-1 flex flex-col">
                            <h3 className="font-bold text-xs truncate mb-2">{product.name}</h3>
                            <div className="flex justify-between items-center mt-auto">
                                <span className="text-emerald-400 font-bold text-sm">€{product.price.toFixed(2)}</span>
                                <button onClick={() => addToCart(product)} className="bg-slate-800 p-2 rounded-xl hover:bg-emerald-500 hover:text-white transition-all"><ShoppingBag size={18} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Cart Sidebar */}
            {isCartOpen && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end">
                    <div className="w-full max-w-sm bg-slate-900 h-full p-6 shadow-2xl flex flex-col animate-slideInRight">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold flex items-center gap-2"><ShoppingCart/> {t.social_post_btn}</h2>
                            <button onClick={() => setIsCartOpen(false)} className="p-2 bg-slate-800 rounded-lg"><X/></button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto space-y-3">
                            {cart.length === 0 && <p className="text-slate-500 text-center py-20 italic">Empty. Add some gear!</p>}
                            {cart.map((item, idx) => (
                                <div key={idx} className="flex gap-4 bg-slate-800/50 p-3 rounded-2xl items-center border border-slate-700/50">
                                    <img src={item.image} className="w-14 h-14 rounded-xl object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm truncate">{item.name}</p>
                                        <p className="text-emerald-400 text-xs font-bold">€{item.price.toFixed(2)}</p>
                                    </div>
                                    <button onClick={() => removeFromCart(idx)} className="text-slate-500 hover:text-red-500 p-2"><X size={18} /></button>
                                </div>
                            ))}
                        </div>

                        {/* Referral Discount Toggle */}
                        {referralDiscountPercent > 0 && cart.some(p => p.isReferralEligible) && (
                            <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                                <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center gap-2">
                                        <Ticket size={18} className="text-emerald-500"/>
                                        <span className="text-xs font-bold">{t.ref_stat_discount}</span>
                                    </div>
                                    <span className="text-emerald-400 font-black">{referralDiscountPercent}%</span>
                                </div>
                                <button 
                                    onClick={() => setUseReferralDiscount(!useReferralDiscount)}
                                    className={`w-full py-2 rounded-xl text-xs font-black transition-all border ${useReferralDiscount ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-transparent text-emerald-500 border-emerald-500/30'}`}
                                >
                                    {useReferralDiscount ? t.ref_max_discount : t.ref_apply_discount}
                                </button>
                            </div>
                        )}

                        <div className="border-t border-slate-800 pt-6 mt-6 space-y-4">
                            <div className="space-y-1">
                                <div className="flex justify-between text-slate-500 text-sm">
                                    <span>Subtotal</span>
                                    <span>€{subtotal.toFixed(2)}</span>
                                </div>
                                {discountApplied > 0 && (
                                    <div className="flex justify-between text-emerald-400 text-sm font-bold">
                                        <span>Referral Discount</span>
                                        <span>-€{discountApplied.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-2xl font-black pt-2">
                                    <span>Total</span>
                                    <span className="text-white">€{finalTotal.toFixed(2)}</span>
                                </div>
                            </div>
                            <button 
                                onClick={handleCheckout}
                                disabled={cart.length === 0 || loadingCheckout}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black py-4 rounded-[1.5rem] flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
                            >
                                <CreditCard size={20} /> {loadingCheckout ? '...' : 'Place Order'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
